import apiClient from '../../common/app/api/client';
import type {
  ApplicationStatus,
  CreditCardApplicationProperties,
  CustomerProgramLimitsModel,
  LoanApplicationIntegrationStatusCode,
  LoanApplicationIntegrationStatusType,
} from '../../common/app/consts/types/apiTypes';
import type { HttpErrorResponse } from '../../common/app/types/api';
import { addUidToPath } from '../../common/app/utils/router';

import { CreditCardApiPaths } from './constants';

/** GET a single credit card application by uid. */
export const getCreditCardApplication = (uid: string) =>
  apiClient
    .get<CreditCardApplicationProperties>(
      addUidToPath(CreditCardApiPaths.Application, uid),
    )
    .then(({ data }) => data);

/**
 * GET the active credit card application for the logged in user, or undefined
 * when no active application exists (HTTP 204 or 404).
 */
export const getActiveCreditCardApplication = () =>
  apiClient
    .get<CreditCardApplicationProperties>(CreditCardApiPaths.ActiveApplication)
    .then(({ data }) => data)
    .catch((error: unknown) => {
      if ((error as { status?: number })?.status === 404) {
        return undefined;
      }
      throw error;
    });

/**
 * GET the per-program credit-limit bounds.
 *
 * A static lookup (no application uid): returns one entry per customer program,
 * sorted ascending by max limit.
 * Used to derive the min/max enforced in the credit-limit step instead of hardcoding them.
 * Throws if the response is empty or invalid so the flow surfaces an error page.
 * See BW-10717 / BW-10865.
 */
export const getCustomerPrograms = () =>
  apiClient
    .get<CustomerProgramLimitsModel[]>(CreditCardApiPaths.CustomerPrograms)
    .then(({ data }) => {
      if (!Array.isArray(data) || data.length === 0) {
        throw {
          name: 'ApiError',
          status: 500,
          statusText: 'Internal Server Error',
          ok: false,
        } satisfies HttpErrorResponse;
      }
      return data;
    });

/** POST to initialize a new credit card application. */
export const initCreditCardApplication = () =>
  apiClient
    .post<CreditCardApplicationProperties>(CreditCardApiPaths.Init)
    .then(({ data }) => data);

/** POST an updated credit card application. */
export const updateCreditCardApplication = (
  application: CreditCardApplicationProperties,
) =>
  apiClient
    .post<CreditCardApplicationProperties, CreditCardApplicationProperties>(
      addUidToPath(CreditCardApiPaths.Application, application.uid),
      application,
    )
    .then(({ data }) => data);

/**
 * DELETE a credit card application by uid.
 *
 * Exported ahead of use: part of the complete v2 client surface, not yet
 * wired into the flow (no cancel/abandon step yet).
 */
export const deleteCreditCardApplication = (uid: string) =>
  apiClient
    .delete<CreditCardApplicationProperties>(
      addUidToPath(CreditCardApiPaths.Application, uid),
    )
    .then(({ data }) => data);

/** POST to start the evaluation (scoring) process for an application. */
export const evaluateCreditCardApplication = (uid: string) =>
  apiClient
    .post<CreditCardApplicationProperties>(
      addUidToPath(CreditCardApiPaths.Evaluate, uid),
    )
    .then(({ data }) => data);

/**
 * POST a qualifying credit-limit decrease, which the backend sends straight to
 * Salesforce (skipping evaluation and consent). Responds 400
 * NOT_CREDIT_LIMIT_DECREASE_ERROR when the application is not a decrease.
 */
export const decreaseCreditCardLimit = (uid: string) =>
  apiClient
    .post<CreditCardApplicationProperties>(
      addUidToPath(CreditCardApiPaths.DecreaseLimit, uid),
    )
    .then(({ data }) => data);

/** GET the current status of an application. */
export const getCreditCardApplicationStatus = (uid: string) =>
  apiClient
    .get<ApplicationStatus>(
      addUidToPath(CreditCardApiPaths.ApplicationStatus, uid),
    )
    .then(({ data }) => data);

/**
 * GET the current status for a given integration type.
 *
 * Mirrors new-loan's per-integration status endpoint; used by
 * useIntegrationStatusPolling to wait for CUSTOMER_MASTER_INTEGRATION.
 */
export const getCreditCardIntegrationStatus = (
  uid: string,
  integrationType: LoanApplicationIntegrationStatusType,
) =>
  apiClient
    .get<LoanApplicationIntegrationStatusCode>(
      `${addUidToPath(CreditCardApiPaths.IntegrationStatus, uid)}/${integrationType}`,
    )
    .then(({ data }) => data);
